import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Commande,
  CommandeInput,
  CommandeListQuery,
  CommandeStatut,
  CommandeUpdate,
  DeduireStockInput,
  Facture,
  Mouvement,
  Paiement,
  PaiementInput,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { cleDashboard } from '@/features/dashboard/api/dashboard.api';
import { clesClients } from '@/features/clients/api/clients.api';

export type CommandeDetaillee = Commande & {
  paiements: Paiement[];
  mouvements: Mouvement[];
};

export type EtatPaiements = {
  paiements: Paiement[];
  avance: number;
  reste: number;
  total: number;
};

export const clesCommandes = {
  toutes: ['commandes'] as const,
  liste: (q: Partial<CommandeListQuery>) => ['commandes', 'liste', q] as const,
  une: (id: string) => ['commandes', id] as const,
  societes: ['commandes', 'societes'] as const,
};

export function useCommandes(q: Partial<CommandeListQuery>) {
  return useQuery({
    queryKey: clesCommandes.liste(q),
    queryFn: () => api.liste<Commande>('/commandes', q),
    placeholderData: (precedent) => precedent,
  });
}

export function useCommande(id: string | null) {
  return useQuery({
    queryKey: clesCommandes.une(id ?? ''),
    queryFn: () => api.get<CommandeDetaillee>(`/commandes/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Sociétés déjà rencontrées — alimente le filtre de la liste.
 *
 * Gardée très longtemps : cette liste ne bouge qu'à la création d'une commande
 * portant une société inédite, et toute écriture invalide déjà `['commandes']`
 * dont cette clé est un descendant. La fraîcheur vient donc de l'invalidation,
 * pas de l'expiration — et chaque relecture inutile coûtait un parcours de la
 * colonne `societe` sur toute la table.
 */
export function useSocietes() {
  return useQuery({
    queryKey: clesCommandes.societes,
    queryFn: () => api.get<string[]>('/commandes/societes'),
    staleTime: 24 * 60 * 60_000,
  });
}

/**
 * Une commande touche aux clients (rattachement), au stock (déduction) et au
 * tableau de bord (CA, restes) : tout est invalidé ensemble.
 */
function useInvalider() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: clesCommandes.toutes });
    void client.invalidateQueries({ queryKey: clesClients.tous });
    void client.invalidateQueries({ queryKey: cleDashboard });
    void client.invalidateQueries({ queryKey: ['produits'] });
    void client.invalidateQueries({ queryKey: ['mouvements'] });
  };
}

export function useCreerCommande() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (input: CommandeInput) => api.post<Commande>('/commandes', input),
    onSuccess: invalider,
  });
}

export function useModifierCommande() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CommandeUpdate }) =>
      api.patch<Commande>(`/commandes/${id}`, patch),
    onSuccess: invalider,
  });
}

export function useChangerStatut() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: CommandeStatut }) =>
      api.post<Commande>(`/commandes/${id}/statut`, { statut }),
    onSuccess: invalider,
  });
}

export function useSupprimerCommande() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ supprime: boolean }>(`/commandes/${id}`),
    onSuccess: invalider,
  });
}

export function useAjouterPaiement() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PaiementInput }) =>
      api.post<EtatPaiements>(`/commandes/${id}/paiements`, input),
    onSuccess: invalider,
  });
}

export function useSupprimerPaiement() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (paiementId: string) => api.delete<EtatPaiements>(`/paiements/${paiementId}`),
    onSuccess: invalider,
  });
}

/** Déduction de stock rattachée à la commande (règle 11). */
export function useDeduireStock() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DeduireStockInput }) =>
      api.post<Mouvement[]>(`/commandes/${id}/stock`, input),
    onSuccess: invalider,
  });
}

export function useAnnulerDeduction() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, mouvementId }: { id: string; mouvementId: string }) =>
      api.delete<Mouvement[]>(`/commandes/${id}/stock/${mouvementId}`),
    onSuccess: invalider,
  });
}

export function useGenererFacture() {
  const invalider = useInvalider();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Facture>(`/commandes/${id}/facture`),
    onSuccess: () => {
      invalider();
      void client.invalidateQueries({ queryKey: ['factures'] });
    },
  });
}
